import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Cuisine, fetchCuisine } from "../services/cuisineService";
import { Skeleton } from "@/components/ui/skeleton";
import { Utensils, Coffee, CakeSlice } from "lucide-react";

// Map dish names to appropriate image URLs
const getDishImage = (dishName: string): string => {
  const dishImageMap: Record<string, string> = {
    "Baklava": "/images/cuisine/baklava.jpg",
    "Kebab": "https://images.unsplash.com/photo-1583224944844-5b268c057b72?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    "Köfte": "https://images.unsplash.com/photo-1624162147446-5a67e0a93032?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    "Turkish Delight": "https://images.unsplash.com/photo-1593346230441-b71f8e93549e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    "Lahmacun": "https://images.unsplash.com/photo-1615368860098-abae1f90d2f5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    "Pide": "https://images.unsplash.com/photo-1616626082216-17733ff0ca74?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    "Menemen": "https://images.unsplash.com/photo-1661606220672-3a56ba64091c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    "Manti": "https://images.unsplash.com/photo-1566740933430-9b9b7d7ba53f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
  };
  
  // Check if we have a specific image for this dish
  if (dishName.toLowerCase() in dishImageMap) {
    return dishImageMap[dishName.toLowerCase()];
  }
  
  // For dishes where we don't have exact matches, try partial matches
  for (const [key, url] of Object.entries(dishImageMap)) {
    if (dishName.toLowerCase().includes(key.toLowerCase())) {
      return url;
    }
  }
  
  // Default image if no match found
  return "/images/cuisine/breakfast.jpg";
};

export default function TurkishCuisine() {
  const [cuisines, setCuisines] = useState<Cuisine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCuisines = async () => {
      try {
        setIsLoading(true);
        const data = await fetchCuisine();
        
        // Ensure all cuisines have appropriate images
        const enhancedCuisines = data.map(cuisine => {
          if (cuisine.category === "dish") {
            return {
              ...cuisine,
              imageUrl: getDishImage(cuisine.name)
            };
          }
          return cuisine;
        });
        
        // Remove duplicates
        const uniqueCuisines = Array.from(
          new Map(enhancedCuisines.map(cuisine => [cuisine.name, cuisine])).values()
        );
        
        setCuisines(uniqueCuisines);
      } catch (err) {
        console.error("Error fetching cuisine data:", err);
        setError("Failed to load cuisine data");
      } finally {
        setIsLoading(false);
      }
    };

    loadCuisines();
  }, []);

  if (error) {
    return (
      <section className="py-12 bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-sans font-bold text-[#333333] mb-2 text-center">Turkish Cuisine</h2>
          <p className="text-center text-red-500 mb-8">Failed to load cuisine data. Please try again later.</p>
        </div>
      </section>
    );
  }

  const dishImages = cuisines.filter(cuisine => cuisine.category === "dish") || [];
  const traditions = cuisines.filter(cuisine => cuisine.category === "tradition") || [];

  return (
    <section className="py-12 bg-[#F5F5F5]">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-sans font-bold text-[#333333] mb-2 text-center">Turkish Cuisine</h2>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          Discover the rich flavors and culinary traditions that make Turkish food world-famous.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Food Images Grid */}
          <div className="bg-white rounded-lg overflow-hidden shadow-md">
            <div className="p-5 border-t border-gray-200">
              <h3 className="text-xl font-sans font-bold text-[#333333] mb-3">Iconic Dishes</h3>
              <p className="text-gray-600 mb-4">
                From kebabs and köfte to baklava and Turkish delight, explore the must-try dishes from every region of Turkey.
              </p>
              <Link href="/cuisine">
                <span className="text-[#30D5C8] font-sans font-semibold hover:text-[#E30A17] transition duration-200 cursor-pointer">
                  Discover Turkish Dishes <span className="ml-1">→</span>
                </span>
              </Link>
            </div>
            {/* <div className="grid grid-cols-2 gap-2 p-4">
              {isLoading ? (
                // Skeleton loading state
                Array(2).fill(0).map((_, index) => (
                  <Skeleton key={index} className="h-36 rounded-lg" />
                ))
              ) : (
                // Food images with appropriate images for each dish
                dishImages.slice(0, 2).map((dish, index) => (
                  <div key={dish.id} className="h-36 rounded-lg overflow-hidden">
                    <img 
                      src={dish.imageUrl} 
                      alt={dish.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))
              )}
            </div> */}
          </div>
          
          {/* Culinary Traditions */}
          <div className="bg-white rounded-lg overflow-hidden shadow-md">
            <div className="p-5">
              <h3 className="text-xl font-sans font-bold text-[#333333] mb-3">Culinary Traditions</h3>
              <p className="text-gray-600 mb-4">
                Turkish cuisine is a rich tapestry of flavors influenced by Ottoman, Middle Eastern, Mediterranean, and Balkan culinary traditions.
              </p>
              
              {isLoading ? (
                // Skeleton loading state
                <div className="space-y-4">
                  {Array(3).fill(0).map((_, index) => (
                    <div key={index} className="flex items-start">
                      <Skeleton className="h-6 w-6 mr-3" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-40 mb-2" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="space-y-3 mb-5">
                  {traditions.slice(0, 3).map((tradition, index) => {
                    // Select an icon based on the tradition
                    let icon;
                    if (tradition.name.toLowerCase().includes("breakfast")) {
                      icon = <Utensils className="text-[#E30A17] mt-1 mr-3" />;
                    } else if (tradition.name.toLowerCase().includes("tea") || tradition.name.toLowerCase().includes("coffee")) {
                      icon = <Coffee className="text-[#E30A17] mt-1 mr-3" />;
                    } else {
                      icon = <CakeSlice className="text-[#E30A17] mt-1 mr-3" />;
                    }
                    
                    return (
                      <li key={tradition.id} className="flex items-start">
                        {icon}
                        <div>
                          <h4 className="font-sans font-semibold">{tradition.name}</h4>
                          <p className="text-sm text-gray-600">{tradition.description}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              
              <Link href="/cuisine">
                <span className="text-[#30D5C8] font-sans font-semibold hover:text-[#E30A17] transition duration-200 cursor-pointer">
                  Explore Food Culture <span className="ml-1">→</span>
                </span>
              </Link>
            </div>
            {/* <div className="grid grid-cols-2 gap-2 p-4">
              {isLoading ? (
                // Skeleton loading state
                Array(2).fill(0).map((_, index) => (
                  <Skeleton key={index} className="h-36 rounded-lg" />
                ))
              ) : (
                // Food images with appropriate images for each dish
                dishImages.slice(0, 2).map((dish, index) => (
                  <div key={dish.id} className="h-36 rounded-lg overflow-hidden">
                    <img 
                      src={dish.imageUrl} 
                      alt={dish.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))
              )}
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
}
